import { Injectable } from '@angular/core';
import {
  ActivatedRoute,
  Data,
  NavigationEnd,
  Params,
  Router,
} from '@angular/router';
import { ConnectableObservable, Observable, ReplaySubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RouterHelperService {
  public readonly routerData = new ConnectableObservable(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.getRoutesData(this.activatedRoute.root))
    ),
    () => new ReplaySubject<Data>(1)
  );

  public readonly routerParams = new ConnectableObservable(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.getRoutesParams(this.activatedRoute.root))
    ),
    () => new ReplaySubject<Params>(1)
  );

  public getData<T>(key: string, defaultData?: T): Observable<T> {
    return this.routerData.pipe(map((data) => data[key] ?? defaultData));
  }

  public getParam<T>(key: string, defaultData?: T): Observable<T> {
    return this.routerParams.pipe(map((data) => data[key] ?? defaultData));
  }

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {
    this.routerData.connect();
    this.routerParams.connect();
  }

  private getRoutesData(route: ActivatedRoute): Data {
    if (route.children.length === 0) {
      return route.snapshot.data;
    }

    const childrenData = route.children.reduce(
      (acc, child) => ({ ...acc, ...this.getRoutesData(child) }),
      {}
    );
    return { ...route.snapshot.data, ...childrenData };
  }

  private getRoutesParams(route: ActivatedRoute): Params {
    if (route.children.length === 0) {
      return route.snapshot.params;
    }

    const childrenData = route.children.reduce(
      (acc, child) => ({ ...acc, ...this.getRoutesParams(child) }),
      {}
    );
    return { ...route.snapshot.params, ...childrenData };
  }
}
